import { z } from "zod";

export const PLATFORM_NAME = "Mero Perks";
export const PLATFORM_WEBSITE = "https://meroperks.com";

export const tenantStatuses = ["trial", "active", "suspended", "cancelled", "archived"] as const;
export const tenantApplicationStatuses = ["PENDING_REVIEW", "APPROVED", "REJECTED"] as const;
export const tenantRoles = [
  "owner",
  "administrator",
  "marketing_manager",
  "location_manager",
  "cashier",
  "analyst",
] as const;

export type TenantRole = (typeof tenantRoles)[number];
export type Permission =
  | "customer.read"
  | "customer.write"
  | "customer.export"
  | "loyalty.adjust"
  | "coupon.create"
  | "coupon.redeem"
  | "campaign.create"
  | "campaign.send"
  | "reward.manage"
  | "reward.read"
  | "reward.create"
  | "reward.edit"
  | "reward.approve"
  | "reward.publish"
  | "reward.issue.manual"
  | "reward.redeem"
  | "reward.reverse"
  | "reward.analytics"
  | "reward.budget.manage"
  | "reward.points.configure"
  | "reward.automation.manage"
  | "reward.campaign.manage"
  | "reward.audit.read"
  | "staff.manage"
  | "report.view"
  | "tenant.settings.manage"
  | "transaction.create"
  | "transaction.confirm"
  | "transaction.approve_high_value"
  | "transaction.reverse"
  | "receipt.review"
  | "transaction.export"
  | "risk.review"
  | "checkout.settings.manage"
  | "mero_credits.read"
  | "mero_credits.purchase"
  | "mero_credits.settlement.read";

export interface TenantContext {
  sessionType: "tenant_staff";
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: TenantRole[];
  permissions: Permission[];
  allowedLocationIds: string[];
}

export interface CustomerContext {
  sessionType: "customer";
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface SuperAdminContext {
  sessionType: "super_admin";
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
}

export type AuthContext = TenantContext | CustomerContext | SuperAdminContext;

export interface ApiSuccess<T> {
  success: true;
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiFailure {
  success: false;
  error: { code: string; message: string; requestId: string };
}

export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email()),
  password: z.string().min(12).max(128),
  turnstileToken: z.string().min(1).optional(),
});

export const tenantSlugSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const tenantLoginSchema = loginSchema.extend({
  tenantSlug: tenantSlugSchema,
});

export const customerRegistrationSchema = loginSchema.extend({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  termsAccepted: z.literal(true).default(true),
  marketingEmail: z.boolean().optional(),
  birthday: z.iso
    .date()
    .refine(
      (value) => value <= new Date().toISOString().slice(0, 10),
      "Birthday cannot be in the future.",
    )
    .optional(),
});

export const emailVerificationSchema = z.object({
  token: z.string().trim().min(32).max(256),
});

export const emailVerificationRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email()),
  portal: z.enum(["customer", "tenant"]),
});

export const passwordResetSchema = z.object({
  token: z.string().trim().min(32).max(256),
  password: z.string().min(12).max(128),
});

export const superAdminBootstrapSchema = loginSchema.omit({ turnstileToken: true }).extend({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
});

export const publicRewardsQuerySchema = z.object({
  tenantSlug: tenantSlugSchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(24),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});

export const redemptionSchema = z.object({
  idempotencyKey: z.string().trim().min(8).max(128),
  mechanismSelectionId: z.string().trim().min(1).max(80).optional(),
});

export const subscriptionPlanCodeSchema = z.enum(["BASIC", "PRO", "MASTER"]);
export const subscriptionBillingCycleSchema = z.enum(["MONTHLY", "ANNUAL"]);
export type SubscriptionPlanCode = z.infer<typeof subscriptionPlanCodeSchema>;
export type SubscriptionBillingCycle = z.infer<typeof subscriptionBillingCycleSchema>;

export type SubscriptionFeature =
  | "loyalty_rules"
  | "redemption"
  | "punch_cards"
  | "inventory"
  | "quick_offers"
  | "basic_analytics"
  | "mero_credits"
  | "segments"
  | "campaigns"
  | "advanced_analytics"
  | "automations"
  | "data_exports"
  | "priority_support";

export interface SubscriptionPlanDefinition {
  code: SubscriptionPlanCode;
  planId: string;
  name: string;
  tagline: string;
  monthlyPrice: number;
  annualPrice: number;
  trialDays: number;
  popular: boolean;
  features: readonly SubscriptionFeature[];
  displayFeatures: readonly string[];
  limits: {
    locations: number | null;
    members: number | null;
    staffUsers: number | null;
    quickOffers: number | null;
    inventoryItems: number | null;
    campaigns: number | null;
    automations: number | null;
  };
}

export const SUBSCRIPTION_PLANS: readonly SubscriptionPlanDefinition[] = [
  {
    code: "BASIC",
    planId: "plan-basic",
    name: "Basic",
    tagline: "Start rewarding regular customers at no cost.",
    monthlyPrice: 0,
    annualPrice: 0,
    trialDays: 0,
    popular: false,
    features: [
      "loyalty_rules",
      "redemption",
      "punch_cards",
      "inventory",
      "quick_offers",
      "basic_analytics",
    ],
    displayFeatures: [
      "Reward Point earning and redemption rules",
      "Digital visit and purchase punch cards",
      "Up to 5 active Quick Offers",
      "1 business location and 2 staff accounts",
      "100 inventory items and 500 members",
      "Basic checkout and performance summary",
    ],
    limits: {
      locations: 1,
      members: 500,
      staffUsers: 2,
      quickOffers: 5,
      inventoryItems: 100,
      campaigns: 0,
      automations: 0,
    },
  },
  {
    code: "PRO",
    planId: "plan-pro",
    name: "Pro",
    tagline: "Grow repeat business with targeted loyalty tools.",
    monthlyPrice: 300,
    annualPrice: 3500,
    trialDays: 7,
    popular: true,
    features: [
      "loyalty_rules",
      "redemption",
      "punch_cards",
      "inventory",
      "quick_offers",
      "basic_analytics",
      "mero_credits",
      "segments",
      "campaigns",
      "advanced_analytics",
    ],
    displayFeatures: [
      "Everything in Basic",
      "Mero Credits acceptance",
      "Customer segments and targeted campaigns",
      "Up to 100 active Quick Offers",
      "5 locations and 15 staff accounts",
      "2,500 inventory items and 5,000 members",
      "Advanced loyalty analytics",
    ],
    limits: {
      locations: 5,
      members: 5_000,
      staffUsers: 15,
      quickOffers: 100,
      inventoryItems: 2_500,
      campaigns: 20,
      automations: 0,
    },
  },
  {
    code: "MASTER",
    planId: "plan-master",
    name: "Master",
    tagline: "Run a complete multi-location loyalty operation.",
    monthlyPrice: 500,
    annualPrice: 5500,
    trialDays: 7,
    popular: false,
    features: [
      "loyalty_rules",
      "redemption",
      "punch_cards",
      "inventory",
      "quick_offers",
      "basic_analytics",
      "mero_credits",
      "segments",
      "campaigns",
      "advanced_analytics",
      "automations",
      "data_exports",
      "priority_support",
    ],
    displayFeatures: [
      "Everything in Pro",
      "Unlimited locations, members, offers, and inventory",
      "Unlimited staff accounts and campaigns",
      "Lifecycle automations",
      "Data exports and full reporting",
      "Priority platform support",
    ],
    limits: {
      locations: null,
      members: null,
      staffUsers: null,
      quickOffers: null,
      inventoryItems: null,
      campaigns: null,
      automations: null,
    },
  },
] as const;

export function subscriptionPlan(code: SubscriptionPlanCode): SubscriptionPlanDefinition {
  const plan = SUBSCRIPTION_PLANS.find((candidate) => candidate.code === code);
  if (!plan) throw new Error(`Unknown subscription plan: ${code}`);
  return plan;
}

export const tenantCreateSchema = z.object({
  slug: tenantSlugSchema,
  businessName: z.string().trim().min(2).max(160),
  legalName: z.string().trim().min(2).max(160).optional(),
  businessType: z.string().trim().min(2).max(80),
  businessEmail: z.string().trim().toLowerCase().pipe(z.email()),
  businessPhone: z.string().trim().min(4).max(40).optional(),
  website: z.url().optional(),
  defaultCurrency: z
    .string()
    .trim()
    .length(3)
    .transform((value) => value.toUpperCase())
    .default("NPR"),
  defaultTimezone: z.string().trim().min(1).max(80).default("UTC"),
  country: z.string().trim().min(2).max(80),
  address: z.string().trim().max(240).optional(),
  city: z.string().trim().max(100).optional(),
  stateProvince: z.string().trim().max(100).optional(),
  postalCode: z.string().trim().max(24).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  logoUrl: z.url().optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#C8102E"),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#F7F5F0"),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#F5A623"),
  planId: z.string().trim().min(1).default("plan-starter"),
  ownerEmail: z.string().trim().toLowerCase().pipe(z.email()),
  ownerFirstName: z.string().trim().min(1).max(80),
  ownerLastName: z.string().trim().min(1).max(80),
  initialPassword: z.string().min(12).max(128),
});

export const businessPanNumberSchema = z
  .string()
  .trim()
  .regex(/^\d{9}$/, "Business PAN number must contain exactly 9 digits.");

export const businessPanAvailabilitySchema = z
  .object({ businessPanNumber: businessPanNumberSchema })
  .strict();

export const tenantSelfRegistrationSchema = tenantCreateSchema
  .omit({
    logoUrl: true,
    primaryColor: true,
    secondaryColor: true,
    accentColor: true,
    planId: true,
  })
  .extend({
    // Android 1.12.0 and earlier serialized blank optional fields as JSON null.
    // Keep accepting those clients while newer versions omit blank fields.
    legalName: z.string().trim().min(2).max(160).nullable().optional(),
    businessPhone: z.string().trim().min(4).max(40).nullable().optional(),
    website: z.url().nullable().optional(),
    address: z.string().trim().max(240).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    stateProvince: z.string().trim().max(100).nullable().optional(),
    postalCode: z.string().trim().max(24).nullable().optional(),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
    businessPanNumber: businessPanNumberSchema,
    subscriptionPlan: z.enum(["BASIC", "PRO", "MASTER"]).default("BASIC"),
    billingCycle: z.enum(["MONTHLY", "ANNUAL"]).default("ANNUAL"),
    termsAccepted: z.literal(true),
    turnstileToken: z.string().min(1).optional(),
  })
  .strict()
  .refine((value) => (value.latitude == null) === (value.longitude == null), {
    message: "Latitude and longitude must be provided together.",
    path: ["latitude"],
  });

export const tenantUpdateSchema = z
  .object({
    slug: tenantSlugSchema.optional(),
    businessName: z.string().trim().min(2).max(160).optional(),
    legalName: z.string().trim().min(2).max(160).nullable().optional(),
    businessType: z.string().trim().min(2).max(80).optional(),
    businessEmail: z.string().trim().toLowerCase().pipe(z.email()).optional(),
    businessPhone: z.string().trim().min(4).max(40).nullable().optional(),
    website: z.url().nullable().optional(),
    defaultCurrency: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toUpperCase())
      .optional(),
    defaultTimezone: z.string().trim().min(1).max(80).optional(),
    country: z.string().trim().min(2).max(80).optional(),
    address: z.string().trim().max(240).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    stateProvince: z.string().trim().max(100).nullable().optional(),
    postalCode: z.string().trim().max(24).nullable().optional(),
    logoUrl: z.url().nullable().optional(),
    primaryColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    secondaryColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    accentColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    planId: z.string().trim().min(1).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "At least one field must be supplied.");

export const tenantStatusSchema = z.object({
  status: z.enum(["trial", "active", "suspended", "cancelled"]),
});

export const tenantApplicationDecisionSchema = z
  .object({
    decision: z.enum(["approve", "reject"]),
    note: z.string().trim().max(1_000).nullable().default(null),
  })
  .strict();

export const accountArchiveSchema = z
  .object({
    confirmation: z.string().trim().min(1).max(320),
  })
  .strict();

export const tenantListQuerySchema = z.object({
  status: z.enum(tenantStatuses).optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});

export const platformUserStatuses = ["active", "suspended"] as const;
export const platformUserListStatuses = ["active", "suspended", "archived"] as const;

export const platformUserListQuerySchema = z.object({
  status: z.enum(platformUserListStatuses).optional(),
  role: z.enum(["customer", "tenant_staff", "super_admin"]).optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});

export const platformUserUpdateSchema = z
  .object({
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    email: z.string().trim().toLowerCase().pipe(z.email()).optional(),
    phone: z.string().trim().min(4).max(40).nullable().optional(),
    birthday: z.iso
      .date()
      .refine(
        (value) => value <= new Date().toISOString().slice(0, 10),
        "Birthday cannot be in the future.",
      )
      .nullable()
      .optional(),
    addressLine1: z.string().trim().max(160).nullable().optional(),
    addressLine2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    district: z.string().trim().max(100).nullable().optional(),
    province: z.string().trim().max(100).nullable().optional(),
    country: z.string().trim().max(100).nullable().optional(),
    postalCode: z.string().trim().max(24).nullable().optional(),
    profileImageUrl: z.url().max(700).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "At least one field must be supplied.");

export const platformUserStatusSchema = z.object({
  status: z.enum(platformUserStatuses),
});

export const platformSettingsSchema = z.object({
  platformName: z.string().trim().min(2).max(100),
  supportEmail: z.string().trim().toLowerCase().pipe(z.email()),
  defaultTimezone: z.string().trim().min(1).max(80),
  defaultCountry: z.string().trim().min(2).max(80),
  privacyPolicyUrl: z.url().nullable(),
  termsUrl: z.url().nullable(),
});

export const rewardTypes = [
  "PERCENTAGE_DISCOUNT",
  "FIXED_DISCOUNT",
  "FREE_ITEM",
  "BOGO",
  "BONUS_POINTS",
  "POINTS_MULTIPLIER",
  "POINTS_REDEMPTION",
  "SPEND_AND_GET",
  "VISIT_REWARD",
  "UPGRADE",
  "COMPLIMENTARY_SERVICE",
  "EARLY_ACCESS",
  "EXCLUSIVE_PRICE",
  "MYSTERY_REWARD",
  "CHOICE_REWARD",
  "CUSTOM",
] as const;

const rewardMechanismCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._:-]*$/,
    "Use letters, numbers, periods, underscores, colons, or dashes.",
  );

const mechanismCodeListSchema = z.array(rewardMechanismCodeSchema).max(100).default([]);

export const rewardProductRulesSchema = z
  .object({
    includeProductCodes: mechanismCodeListSchema,
    excludeProductCodes: mechanismCodeListSchema,
    includeCategoryCodes: mechanismCodeListSchema,
    excludeCategoryCodes: mechanismCodeListSchema,
  })
  .strict()
  .superRefine((rules, context) => {
    const assertNoOverlap = (included: string[], excluded: string[], path: string) => {
      const excludedCodes = new Set(excluded.map((code) => code.toLocaleUpperCase()));
      if (included.some((code) => excludedCodes.has(code.toLocaleUpperCase()))) {
        context.addIssue({
          code: "custom",
          message: "A code cannot be both included and excluded.",
          path: [path],
        });
      }
    };
    assertNoOverlap(rules.includeProductCodes, rules.excludeProductCodes, "includeProductCodes");
    assertNoOverlap(rules.includeCategoryCodes, rules.excludeCategoryCodes, "includeCategoryCodes");
  });

export const rewardMechanismOptionSchema = z
  .object({
    id: rewardMechanismCodeSchema,
    label: z.string().trim().min(1).max(160),
    liabilityMinor: z.number().int().min(0).max(100_000_000_000),
    weight: z.number().int().min(1).max(1_000_000).optional(),
  })
  .strict();

/**
 * Versioned, allowlisted configuration for the concrete benefit behind a
 * reward. Cross-field requirements are enforced alongside rewardType below so
 * historical standard rewards can continue to use an empty configuration.
 */
export const rewardMechanismConfigSchema = z
  .object({
    schemaVersion: z.literal(1).default(1),
    displayTheme: z.enum(["CRIMSON", "GOLD", "CREAM", "NAVY"]).optional(),
    itemLabel: z.string().trim().min(1).max(160).optional(),
    quantity: z.number().int().min(1).max(10_000).optional(),
    estimatedUnitCostMinor: z.number().int().min(0).max(100_000_000_000).optional(),
    productRules: rewardProductRulesSchema.optional(),
    bonusPoints: z.number().int().min(1).max(10_000_000).optional(),
    multiplierBasisPoints: z.number().int().min(10_001).max(100_000).optional(),
    maximumBonusPoints: z.number().int().min(1).max(10_000_000).optional(),
    verifiedTransactionRequired: z.literal(true).optional(),
    spendThresholdMinor: z.number().int().min(1).max(100_000_000_000).optional(),
    benefitLabel: z.string().trim().min(1).max(160).optional(),
    requiredVisitCount: z.number().int().min(1).max(10_000).optional(),
    cooldownDays: z.number().int().min(0).max(3_650).optional(),
    punchMode: z.enum(["VISIT", "ITEM_QUANTITY"]).optional(),
    qualifyingItemLabel: z.string().trim().min(1).max(160).optional(),
    rewardItemLabel: z.string().trim().min(1).max(160).optional(),
    rewardQuantity: z.number().int().min(1).max(10_000).optional(),
    minimumPurchaseMinor: z.number().int().min(0).max(100_000_000_000).optional(),
    repeatable: z.boolean().optional(),
    buyQuantity: z.number().int().min(1).max(10_000).optional(),
    getQuantity: z.number().int().min(1).max(10_000).optional(),
    fromLabel: z.string().trim().min(1).max(160).optional(),
    toLabel: z.string().trim().min(1).max(160).optional(),
    subjectToAvailability: z.boolean().optional(),
    serviceCode: rewardMechanismCodeSchema.optional(),
    serviceLabel: z.string().trim().min(1).max(160).optional(),
    accessLabel: z.string().trim().min(1).max(160).optional(),
    capacity: z.number().int().min(1).max(10_000_000).optional(),
    regularPriceMinor: z.number().int().min(1).max(100_000_000_000).optional(),
    exclusivePriceMinor: z.number().int().min(0).max(100_000_000_000).optional(),
    maximumLiabilityMinor: z.number().int().min(0).max(100_000_000_000).optional(),
    options: z.array(rewardMechanismOptionSchema).min(2).max(50).optional(),
    selectionDeadline: z.iso.datetime().optional(),
    fulfillmentInstructions: z.string().trim().min(1).max(2_000).optional(),
    liabilityMinor: z.number().int().min(0).max(100_000_000_000).optional(),
  })
  .strict();

export type RewardMechanismConfig = z.infer<typeof rewardMechanismConfigSchema>;
export type RewardProductRules = z.infer<typeof rewardProductRulesSchema>;
export type RewardMechanismOption = z.infer<typeof rewardMechanismOptionSchema>;

export const inventoryItemCreateSchema = z
  .object({
    code: rewardMechanismCodeSchema,
    name: z.string().trim().min(1).max(160),
    brand: z.string().trim().min(1).max(120).optional(),
    category: z.string().trim().min(1).max(120),
    priceMinor: z.number().int().min(0).max(100_000_000_000).optional(),
    stockQuantity: z.number().int().min(0).max(100_000_000).optional(),
    imageUrl: z.url().max(2_000).optional(),
    status: z.enum(["active", "inactive"]).default("active"),
  })
  .strict();

export const inventoryItemUpdateSchema = inventoryItemCreateSchema
  .omit({ code: true })
  .partial()
  .strict();
export type InventoryItemCreateInput = z.infer<typeof inventoryItemCreateSchema>;
export type InventoryItemUpdateInput = z.infer<typeof inventoryItemUpdateSchema>;

const hasProductIncludes = (config: RewardMechanismConfig) =>
  (config.productRules?.includeProductCodes.length ?? 0) > 0 ||
  (config.productRules?.includeCategoryCodes.length ?? 0) > 0;

const validateMechanismConfig = (
  rewardType: (typeof rewardTypes)[number],
  config: RewardMechanismConfig | undefined,
  context: z.RefinementCtx,
) => {
  // Missing configs are normalized by the repository only for legacy
  // mechanisms with an unambiguous default. New structured configs are fully
  // validated whenever supplied.
  if (config === undefined) return;
  const requireField = (field: keyof RewardMechanismConfig, message: string) => {
    if (config[field] === undefined)
      context.addIssue({ code: "custom", message, path: ["mechanismConfig", field] });
  };
  const validateUniqueOptions = () => {
    const ids = new Set<string>();
    for (const [index, option] of (config.options ?? []).entries()) {
      const id = option.id.toLocaleUpperCase();
      if (ids.has(id))
        context.addIssue({
          code: "custom",
          message: "Option identifiers must be unique.",
          path: ["mechanismConfig", "options", index, "id"],
        });
      ids.add(id);
    }
  };
  switch (rewardType) {
    case "FREE_ITEM":
      requireField("itemLabel", "A free item label is required.");
      requireField("quantity", "A free item quantity is required.");
      requireField("estimatedUnitCostMinor", "An estimated unit cost is required, including zero.");
      break;
    case "BOGO":
      requireField("buyQuantity", "A buy quantity is required.");
      requireField("getQuantity", "A free quantity is required.");
      requireField("estimatedUnitCostMinor", "An estimated unit cost is required, including zero.");
      if (!hasProductIncludes(config))
        context.addIssue({
          code: "custom",
          message: "BOGO requires at least one included product or category.",
          path: ["mechanismConfig", "productRules"],
        });
      break;
    case "BONUS_POINTS":
      requireField("bonusPoints", "The number of bonus points is required.");
      requireField(
        "verifiedTransactionRequired",
        "Bonus points must require a verified transaction.",
      );
      if (
        config.bonusPoints !== undefined &&
        config.maximumBonusPoints !== undefined &&
        config.maximumBonusPoints > config.bonusPoints
      )
        context.addIssue({
          code: "custom",
          message: "The bonus cap cannot exceed the configured fixed bonus.",
          path: ["mechanismConfig", "maximumBonusPoints"],
        });
      break;
    case "POINTS_MULTIPLIER":
      requireField("multiplierBasisPoints", "A points multiplier greater than 1x is required.");
      requireField("maximumBonusPoints", "A multiplier requires a maximum bonus-points cap.");
      requireField(
        "verifiedTransactionRequired",
        "A points multiplier must require a verified transaction.",
      );
      break;
    case "SPEND_AND_GET":
      requireField("spendThresholdMinor", "A spend threshold is required.");
      requireField("benefitLabel", "A spend-and-get benefit is required.");
      break;
    case "VISIT_REWARD":
      requireField("requiredVisitCount", "A verified visit count is required.");
      requireField("cooldownDays", "A visit cooldown is required, including zero days.");
      requireField("verifiedTransactionRequired", "Visit rewards must use verified transactions.");
      requireField("estimatedUnitCostMinor", "An estimated unit cost is required, including zero.");
      break;
    case "UPGRADE":
      requireField("fromLabel", "The original level is required.");
      requireField("toLabel", "The upgraded level is required.");
      requireField("estimatedUnitCostMinor", "An estimated unit cost is required, including zero.");
      break;
    case "COMPLIMENTARY_SERVICE":
      requireField("serviceCode", "A service code is required.");
      requireField("serviceLabel", "A service label is required.");
      requireField("quantity", "A service quantity is required.");
      requireField("estimatedUnitCostMinor", "An estimated unit cost is required, including zero.");
      break;
    case "EARLY_ACCESS":
      requireField("accessLabel", "An early-access benefit is required.");
      requireField("capacity", "Early-access capacity is required.");
      requireField("estimatedUnitCostMinor", "An estimated unit cost is required, including zero.");
      break;
    case "EXCLUSIVE_PRICE":
      requireField("regularPriceMinor", "The regular price is required.");
      requireField("exclusivePriceMinor", "The exclusive price is required.");
      if (
        config.regularPriceMinor !== undefined &&
        config.exclusivePriceMinor !== undefined &&
        config.exclusivePriceMinor >= config.regularPriceMinor
      )
        context.addIssue({
          code: "custom",
          message: "The exclusive price must be lower than the regular price.",
          path: ["mechanismConfig", "exclusivePriceMinor"],
        });
      if (!hasProductIncludes(config))
        context.addIssue({
          code: "custom",
          message: "An exclusive price requires at least one included product or category.",
          path: ["mechanismConfig", "productRules"],
        });
      break;
    case "MYSTERY_REWARD": {
      requireField("options", "At least two weighted mystery options are required.");
      requireField("maximumLiabilityMinor", "A maximum mystery-reward liability is required.");
      validateUniqueOptions();
      (config.options ?? []).forEach((option, index) => {
        if (option.weight === undefined)
          context.addIssue({
            code: "custom",
            message: "Every mystery option needs a positive weight.",
            path: ["mechanismConfig", "options", index, "weight"],
          });
        if (
          config.maximumLiabilityMinor !== undefined &&
          option.liabilityMinor > config.maximumLiabilityMinor
        ) {
          context.addIssue({
            code: "custom",
            message: "Option liability cannot exceed the configured maximum.",
            path: ["mechanismConfig", "options", index, "liabilityMinor"],
          });
        }
      });
      break;
    }
    case "CHOICE_REWARD":
      requireField("options", "At least two customer choices are required.");
      requireField("selectionDeadline", "A selection deadline is required.");
      validateUniqueOptions();
      break;
    case "CUSTOM":
      requireField("benefitLabel", "A custom benefit label is required.");
      requireField("fulfillmentInstructions", "Custom fulfillment instructions are required.");
      requireField("liabilityMinor", "Custom reward liability is required, including zero.");
      break;
    default:
      break;
  }
};

export const rewardMechanismDefinitionSchema = z
  .object({
    rewardType: z.enum(rewardTypes),
    mechanismConfig: rewardMechanismConfigSchema,
  })
  .superRefine((value, context) => {
    validateMechanismConfig(value.rewardType, value.mechanismConfig, context);
  });

export const rewardObjectives = [
  "ACQUIRE_CUSTOMER",
  "INCREASE_FREQUENCY",
  "INCREASE_SPEND",
  "RETAIN_CUSTOMER",
  "WIN_BACK",
  "CELEBRATE_RECOGNIZE",
  "SERVICE_RECOVERY",
  "PROMOTE_PRODUCT",
  "ENCOURAGE_REFERRAL",
  "CUSTOM",
] as const;

export const rewardLifecycleStatuses = [
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "REJECTED",
  "SCHEDULED",
  "ACTIVE",
  "PAUSED",
  "EXPIRED",
  "ARCHIVED",
] as const;

export const rewardEligibilityRuleSchema = z.object({
  dimension: z.enum([
    "CUSTOMER",
    "MERO_TIER",
    "TRANSACTION",
    "BEHAVIOR",
    "TIME",
    "REWARD_STATE",
    "RISK",
  ]),
  field: z.string().trim().min(1).max(80),
  operator: z.enum(["EQ", "NEQ", "IN", "NOT_IN", "GTE", "LTE", "BETWEEN", "EXISTS", "NOT_EXISTS"]),
  value: z.unknown(),
});

const rewardFields = {
  name: z.string().trim().min(2).max(160),
  internalName: z.string().trim().min(2).max(160).optional(),
  description: z.string().trim().min(2).max(2_000),
  imageUrl: z.url().nullable().optional(),
  rewardType: z.enum(rewardTypes).default("POINTS_REDEMPTION"),
  mechanismConfig: rewardMechanismConfigSchema.optional(),
  objective: z.enum(rewardObjectives).default("RETAIN_CUSTOMER"),
  rewardValueMinor: z.number().int().min(0).max(100_000_000_000).default(0),
  pointsCost: z.number().int().min(0).max(10_000_000).default(0),
  inventoryTotal: z.number().int().min(0).max(10_000_000).default(0),
  minimumSpendMinor: z.number().int().min(0).max(100_000_000_000).default(0),
  maximumDiscountMinor: z.number().int().min(1).max(100_000_000_000).nullable().optional(),
  currencyCode: z
    .string()
    .trim()
    .length(3)
    .transform((value) => value.toUpperCase())
    .optional(),
  expirationMode: z
    .enum(["CAMPAIGN_END", "DAYS_AFTER_ISSUE", "FIXED_DATE", "NEVER"])
    .default("CAMPAIGN_END"),
  expirationDaysAfterIssue: z.number().int().min(1).max(3_650).nullable().optional(),
  eligibleTierCodes: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  eligibleSegmentIds: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
  eligibleLocationPublicIds: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
  channelScope: z
    .array(z.enum(["IN_STORE", "ONLINE", "POS_API", "HOTEL_DESK", "CAFE_COUNTER", "MOBILE_APP"]))
    .min(1)
    .max(10)
    .default(["IN_STORE"]),
  perMemberLimit: z.number().int().min(1).max(10_000).default(1),
  globalIssueLimit: z.number().int().min(1).max(100_000_000).nullable().optional(),
  globalRedemptionLimit: z.number().int().min(1).max(100_000_000).nullable().optional(),
  budgetMinor: z.number().int().min(1).max(100_000_000_000).nullable().optional(),
  stackingMode: z.enum(["EXCLUSIVE", "STACKABLE", "PRIORITY"]).default("EXCLUSIVE"),
  priority: z.number().int().min(1).max(1_000).default(100),
  requiresApproval: z.boolean().default(false),
  termsText: z.string().trim().max(10_000).default(""),
  eligibilityRules: z.array(rewardEligibilityRuleSchema).max(100).default([]),
  status: z.enum(["draft", "active", "inactive"]).default("draft"),
  featured: z.boolean().default(false),
  startsAt: z.iso.datetime().nullable().optional(),
  endsAt: z.iso.datetime().nullable().optional(),
};

export const tenantRewardCreateSchema = z.object(rewardFields).superRefine((value, context) => {
  validateMechanismConfig(value.rewardType, value.mechanismConfig, context);
  if (value.startsAt && value.endsAt && value.startsAt >= value.endsAt) {
    context.addIssue({
      code: "custom",
      message: "The end date must be after the start date.",
      path: ["endsAt"],
    });
  }
  if (value.rewardType === "PERCENTAGE_DISCOUNT" && value.maximumDiscountMinor == null) {
    context.addIssue({
      code: "custom",
      message: "A percentage discount requires a maximum discount.",
      path: ["maximumDiscountMinor"],
    });
  }
  if (
    value.rewardType === "PERCENTAGE_DISCOUNT" &&
    (value.rewardValueMinor < 1 || value.rewardValueMinor > 10_000)
  ) {
    context.addIssue({
      code: "custom",
      message: "Percentage reward value must be 1 to 10,000 basis points.",
      path: ["rewardValueMinor"],
    });
  }
  if (value.expirationMode === "DAYS_AFTER_ISSUE" && value.expirationDaysAfterIssue == null) {
    context.addIssue({
      code: "custom",
      message: "Days-after-issue expiration requires a duration.",
      path: ["expirationDaysAfterIssue"],
    });
  }
});

export const tenantRewardUpdateSchema = z
  .object({
    name: rewardFields.name.optional(),
    internalName: rewardFields.internalName,
    description: rewardFields.description.optional(),
    imageUrl: rewardFields.imageUrl,
    rewardType: z.enum(rewardTypes).optional(),
    mechanismConfig: rewardMechanismConfigSchema.optional(),
    objective: z.enum(rewardObjectives).optional(),
    rewardValueMinor: rewardFields.rewardValueMinor.unwrap().optional(),
    pointsCost: rewardFields.pointsCost.unwrap().optional(),
    inventoryTotal: rewardFields.inventoryTotal.unwrap().optional(),
    minimumSpendMinor: rewardFields.minimumSpendMinor.unwrap().optional(),
    maximumDiscountMinor: rewardFields.maximumDiscountMinor,
    currencyCode: rewardFields.currencyCode,
    expirationMode: z.enum(["CAMPAIGN_END", "DAYS_AFTER_ISSUE", "FIXED_DATE", "NEVER"]).optional(),
    expirationDaysAfterIssue: rewardFields.expirationDaysAfterIssue,
    eligibleTierCodes: rewardFields.eligibleTierCodes.unwrap().optional(),
    eligibleSegmentIds: rewardFields.eligibleSegmentIds.unwrap().optional(),
    eligibleLocationPublicIds: rewardFields.eligibleLocationPublicIds.unwrap().optional(),
    channelScope: rewardFields.channelScope.unwrap().optional(),
    perMemberLimit: rewardFields.perMemberLimit.unwrap().optional(),
    globalIssueLimit: rewardFields.globalIssueLimit,
    globalRedemptionLimit: rewardFields.globalRedemptionLimit,
    budgetMinor: rewardFields.budgetMinor,
    stackingMode: z.enum(["EXCLUSIVE", "STACKABLE", "PRIORITY"]).optional(),
    priority: rewardFields.priority.unwrap().optional(),
    requiresApproval: rewardFields.requiresApproval.unwrap().optional(),
    termsText: rewardFields.termsText.unwrap().optional(),
    eligibilityRules: rewardFields.eligibilityRules.unwrap().optional(),
    status: z.enum(["draft", "active", "inactive"]).optional(),
    featured: z.boolean().optional(),
    startsAt: rewardFields.startsAt,
    endsAt: rewardFields.endsAt,
  })
  .refine((value) => Object.keys(value).length > 0, "At least one field must be supplied.")
  .refine((value) => !value.startsAt || !value.endsAt || value.startsAt < value.endsAt, {
    message: "The end date must be after the start date.",
    path: ["endsAt"],
  })
  .superRefine((value, context) => {
    if (value.rewardType !== undefined && value.mechanismConfig !== undefined) {
      validateMechanismConfig(value.rewardType, value.mechanismConfig, context);
    }
  });

export const tenantRewardListQuerySchema = z.object({
  lifecycleStatus: z.enum(rewardLifecycleStatuses).optional(),
  rewardType: z.enum(rewardTypes).optional(),
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});

export const rewardRejectSchema = z.object({
  reason: z.string().trim().min(2).max(1_000),
});

const rewardPurchaseReferenceSchema = z.string().trim().min(1).max(128);

export const rewardRedemptionLookupSchema = z.object({
  redemptionCode: z.string().trim().min(4).max(128),
  locationPublicId: z.string().trim().min(1).max(120).optional(),
  purchaseAmountMinor: z.number().int().min(0).max(100_000_000_000).optional(),
  purchaseReference: rewardPurchaseReferenceSchema.optional(),
  channel: z
    .enum(["IN_STORE", "ONLINE", "POS_API", "HOTEL_DESK", "CAFE_COUNTER", "MOBILE_APP"])
    .optional(),
  lineItems: z
    .array(
      z
        .object({
          productCode: rewardMechanismCodeSchema.optional(),
          categoryCode: rewardMechanismCodeSchema.optional(),
          quantity: z.number().int().min(1).max(10_000),
        })
        .strict()
        .refine((item) => item.productCode !== undefined || item.categoryCode !== undefined, {
          message: "Each line item needs a product or category code.",
        }),
    )
    .max(100)
    .optional(),
  mechanismSelectionId: rewardMechanismCodeSchema.optional(),
});

export const rewardRedemptionFulfillmentSchema = z.object({
  locationPublicId: z.string().trim().min(1).max(120).optional(),
  purchaseAmountMinor: z.number().int().min(0).max(100_000_000_000).optional(),
  purchaseReference: rewardPurchaseReferenceSchema.optional(),
  channel: z
    .enum(["IN_STORE", "ONLINE", "POS_API", "HOTEL_DESK", "CAFE_COUNTER", "MOBILE_APP"])
    .optional(),
  lineItems: z
    .array(
      z
        .object({
          productCode: rewardMechanismCodeSchema.optional(),
          categoryCode: rewardMechanismCodeSchema.optional(),
          quantity: z.number().int().min(1).max(10_000),
        })
        .strict()
        .refine((item) => item.productCode !== undefined || item.categoryCode !== undefined, {
          message: "Each line item needs a product or category code.",
        }),
    )
    .max(100)
    .optional(),
  mechanismSelectionId: rewardMechanismCodeSchema.optional(),
});

export const rewardRedemptionReverseSchema = z.object({
  reason: z.string().trim().min(2).max(1_000),
});

export const rewardManualIssueSchema = z
  .object({
    customerPublicId: z.string().trim().min(1).max(120),
    locationPublicId: z.string().trim().min(1).max(120),
    reason: z.string().trim().min(2).max(1_000),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .strict();

const rewardSegmentNumber = z.number().int().min(0).max(100_000_000_000);
const rewardSegmentNumericOperator = z.enum(["EQ", "NEQ", "GTE", "LTE"]);
const rewardSegmentRange = z
  .tuple([rewardSegmentNumber, rewardSegmentNumber])
  .refine(([minimum, maximum]) => minimum <= maximum, {
    message: "The segment range minimum must not exceed its maximum.",
  });

const rewardSegmentCustomerNumericBase = z.object({
  dimension: z.literal("CUSTOMER"),
  field: z.enum([
    "membershipAgeDays",
    "verifiedTransactionCount",
    "verifiedSpendMinor",
    "daysSinceLastVerifiedTransaction",
  ]),
});

const rewardSegmentTransactionNumericBase = z.object({
  dimension: z.literal("TRANSACTION"),
  field: z.enum(["count", "totalSpendMinor", "daysSinceLast"]),
});

const rewardSegmentNumericRuleSchema = z.union([
  rewardSegmentCustomerNumericBase
    .extend({
      operator: rewardSegmentNumericOperator,
      value: rewardSegmentNumber,
    })
    .strict(),
  rewardSegmentCustomerNumericBase
    .extend({
      operator: z.literal("BETWEEN"),
      value: rewardSegmentRange,
    })
    .strict(),
  rewardSegmentTransactionNumericBase
    .extend({
      operator: rewardSegmentNumericOperator,
      value: rewardSegmentNumber,
    })
    .strict(),
  rewardSegmentTransactionNumericBase
    .extend({
      operator: z.literal("BETWEEN"),
      value: rewardSegmentRange,
    })
    .strict(),
]);

export const rewardSegmentRuleSchema = z.union([
  z
    .object({
      dimension: z.literal("CUSTOMER"),
      field: z.literal("lifecycleCohort"),
      operator: z.enum(["IN", "NOT_IN"]),
      value: z
        .array(
          z.enum([
            "ALL_MEMBERS",
            "NEW_MEMBERS",
            "FIRST_TIME_BUYERS",
            "REPEAT_CUSTOMERS",
            "BIRTHDAY",
            "ANNIVERSARY",
          ]),
        )
        .min(1)
        .max(6),
    })
    .strict(),
  z
    .object({
      dimension: z.literal("CUSTOMER"),
      field: z.literal("emailVerified"),
      operator: z.enum(["EQ", "NEQ"]),
      value: z.boolean(),
    })
    .strict(),
  rewardSegmentNumericRuleSchema,
]);

export const rewardSegmentConditionsSchema = z
  .object({
    match: z.enum(["ALL", "ANY"]),
    rules: z.array(rewardSegmentRuleSchema).min(1).max(12),
  })
  .strict();

export const rewardSegmentCreateSchema = z
  .object({
    name: z.string().trim().min(2).max(160),
    description: z.string().trim().max(1_000).default(""),
    conditions: rewardSegmentConditionsSchema,
    status: z.enum(["draft", "active"]).default("draft"),
  })
  .strict();

export const rewardSegmentUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(160).optional(),
    description: z.string().trim().max(1_000).optional(),
    conditions: rewardSegmentConditionsSchema.optional(),
    status: z.enum(["draft", "active"]).optional(),
  })
  .strict()
  .refine((value) => Object.values(value).some((item) => item !== undefined), {
    message: "At least one segment field must be updated.",
  });

export type RewardSegmentRule = z.infer<typeof rewardSegmentRuleSchema>;
export type RewardSegmentConditions = z.infer<typeof rewardSegmentConditionsSchema>;
export type RewardSegmentCreateInput = z.infer<typeof rewardSegmentCreateSchema>;
export type RewardSegmentUpdateInput = z.infer<typeof rewardSegmentUpdateSchema>;

export const rewardCampaignAudienceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ALL_ACTIVE_MEMBERS") }).strict(),
  z.object({ type: z.literal("SEGMENT") }).strict(),
]);

export const rewardCampaignCreateSchema = z
  .object({
    name: z.string().trim().min(2).max(160),
    objective: z.enum(rewardObjectives),
    rewardPublicId: z.string().trim().min(1).max(120).nullable().optional(),
    segmentPublicId: z.string().trim().min(1).max(120).nullable().optional(),
    audience: rewardCampaignAudienceSchema.default({ type: "ALL_ACTIVE_MEMBERS" }),
    channelScope: z.tuple([z.literal("IN_APP")]).default(["IN_APP"]),
    budgetMinor: z.number().int().min(1).max(100_000_000_000).nullable().optional(),
    startsAt: z.iso.datetime().nullable().optional(),
    endsAt: z.iso.datetime().nullable().optional(),
    timezone: z.string().trim().min(1).max(80),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.startsAt && value.endsAt && value.startsAt >= value.endsAt) {
      context.addIssue({
        code: "custom",
        message: "The end date must be after the start date.",
        path: ["endsAt"],
      });
    }
    const hasSegment = value.segmentPublicId !== undefined && value.segmentPublicId !== null;
    if (value.audience.type === "SEGMENT" && !hasSegment) {
      context.addIssue({
        code: "custom",
        message: "A segment audience requires segmentPublicId.",
        path: ["segmentPublicId"],
      });
    }
    if (value.audience.type === "ALL_ACTIVE_MEMBERS" && hasSegment) {
      context.addIssue({
        code: "custom",
        message: "A segment-backed campaign must use the SEGMENT audience type.",
        path: ["audience"],
      });
    }
  });

export const rewardCampaignLifecycleStatuses = [
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "SCHEDULED",
  "ACTIVE",
  "PAUSED",
  "COMPLETED",
  "REJECTED",
  "ARCHIVED",
] as const;

export const rewardCampaignExecutionLimitsSchema = z
  .object({
    quantityLimit: z.number().int().min(1).max(100_000_000).nullable(),
    budgetMinor: z.number().int().min(1).max(100_000_000_000).nullable(),
  })
  .strict();

export const rewardCampaignTransitionSchema = z
  .object({
    target: z.enum(rewardCampaignLifecycleStatuses),
  })
  .strict();

export const rewardReferralStatuses = [
  "PENDING",
  "QUALIFIED",
  "REWARDED",
  "REJECTED",
  "CANCELLED",
] as const;

const rewardReferralPublicReferenceSchema = z.string().trim().min(1).max(120);

export const rewardReferralCreateSchema = z
  .object({
    referrerCustomerPublicId: rewardReferralPublicReferenceSchema,
    referredCustomerPublicId: rewardReferralPublicReferenceSchema,
    referrerRewardPublicId: rewardReferralPublicReferenceSchema,
    referredRewardPublicId: rewardReferralPublicReferenceSchema.nullable().default(null),
    campaignPublicId: rewardReferralPublicReferenceSchema.nullable().default(null),
    minimumSpendMinor: z.number().int().min(0).max(100_000_000_000),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .strict()
  .refine((value) => value.referrerCustomerPublicId !== value.referredCustomerPublicId, {
    message: "A member cannot refer themselves.",
    path: ["referredCustomerPublicId"],
  });

export const rewardReferralListQuerySchema = z
  .object({
    status: z.enum(rewardReferralStatuses).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).max(10_000).default(0),
  })
  .strict();

export type RewardCampaignLifecycleStatus = (typeof rewardCampaignLifecycleStatuses)[number];
export type RewardCampaignAudience = z.infer<typeof rewardCampaignAudienceSchema>;
export type RewardReferralStatus = (typeof rewardReferralStatuses)[number];
export type RewardCampaignExecutionLimitsInput = z.infer<
  typeof rewardCampaignExecutionLimitsSchema
>;
export type RewardCampaignTransitionInput = z.infer<typeof rewardCampaignTransitionSchema>;
export type RewardReferralCreateInput = z.infer<typeof rewardReferralCreateSchema>;
export type RewardReferralListQuery = z.infer<typeof rewardReferralListQuerySchema>;

export const rewardAutomationTemplateCodes = [
  "WELCOME",
  "FIRST_PURCHASE",
  "BIRTHDAY",
  "MEMBERSHIP_ANNIVERSARY",
  "MILESTONE",
  "INACTIVITY",
  "REWARD_EXPIRING",
  "POINTS_EXPIRING",
  "TIER_STATUS",
  "REFERRAL_QUALIFIED",
  "SPECIAL_EVENT",
  "SERVICE_RECOVERY",
] as const;

export const rewardAutomationEditableStatuses = ["DRAFT", "ACTIVE", "PAUSED"] as const;
export const rewardAutomationLifecycleStatuses = [
  ...rewardAutomationEditableStatuses,
  "ARCHIVED",
] as const;

const rewardAutomationCadenceSchema = z.enum(["DAILY", "HOURLY"]);
const rewardAutomationChannelSchema = z.enum(["IN_APP", "EMAIL", "PUSH", "SMS"]);
const rewardAutomationChannelsSchema = z
  .array(rewardAutomationChannelSchema)
  .min(1)
  .max(4)
  .refine((channels) => new Set(channels).size === channels.length, {
    message: "Automation communication channels must not contain duplicates.",
  });
const rewardAutomationReasonSchema = z.string().trim().min(2).max(1_000);
const rewardAutomationIssueActionSchema = z
  .object({
    type: z.literal("ISSUE_REWARD"),
    reason: rewardAutomationReasonSchema,
    channels: rewardAutomationChannelsSchema,
  })
  .strict();
const rewardAutomationReminderActionSchema = z
  .object({
    type: z.literal("SEND_REMINDER"),
    reason: rewardAutomationReasonSchema,
    channels: rewardAutomationChannelsSchema,
  })
  .strict();

const isValidAutomationTimezone = (value: string) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
};

const isValidAutomationCalendarDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const rewardAutomationNameSchema = z.string().trim().min(2).max(160);
const rewardAutomationPublicReferenceSchema = z.string().trim().min(1).max(120);
const rewardAutomationTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .refine(isValidAutomationTimezone, {
    message: "Automation timezone must be a valid IANA timezone.",
  });
const rewardAutomationFrequencyCapSchema = z.number().int().min(1).max(3_650).nullable().optional();
const rewardAutomationNextRunSchema = z.iso.datetime().nullable().optional();
const rewardAutomationCommonShape = {
  name: rewardAutomationNameSchema,
  rewardPublicId: rewardAutomationPublicReferenceSchema.nullable().optional(),
  timezone: rewardAutomationTimezoneSchema,
  frequencyCapDays: rewardAutomationFrequencyCapSchema,
  status: z.enum(rewardAutomationEditableStatuses).default("DRAFT"),
  nextRunAt: rewardAutomationNextRunSchema,
};

const rewardAutomationWelcomeTriggerSchema = z
  .object({
    cadence: rewardAutomationCadenceSchema,
    requireFirstPurchase: z.boolean(),
    includeHistorical: z.boolean(),
  })
  .strict();
const rewardAutomationFirstPurchaseTriggerSchema = z
  .object({
    cadence: rewardAutomationCadenceSchema,
    includeHistorical: z.boolean(),
  })
  .strict();
const rewardAutomationCalendarTriggerSchema = z.discriminatedUnion("timing", [
  z
    .object({
      cadence: rewardAutomationCadenceSchema,
      timing: z.literal("DAYS_BEFORE"),
      daysBefore: z.number().int().min(0).max(365),
      leapDayBehavior: z.enum(["FEB_28", "MAR_01", "EXACT_DATE_ONLY"]),
    })
    .strict(),
  z
    .object({
      cadence: rewardAutomationCadenceSchema,
      timing: z.enum([
        "EXACT_DATE",
        "BIRTHDAY_WEEK",
        "BIRTHDAY_MONTH",
        "FIRST_DAY_OF_BIRTHDAY_MONTH",
      ]),
      daysBefore: z.literal(0),
      leapDayBehavior: z.enum(["FEB_28", "MAR_01", "EXACT_DATE_ONLY"]),
    })
    .strict(),
]);
const rewardAutomationMilestoneTriggerSchema = z.discriminatedUnion("metric", [
  z
    .object({
      cadence: rewardAutomationCadenceSchema,
      metric: z.enum(["PURCHASE_COUNT", "SPEND_MINOR"]),
      threshold: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
      includeHistorical: z.boolean(),
    })
    .strict(),
  z
    .object({
      cadence: rewardAutomationCadenceSchema,
      metric: z.literal("VISIT_COUNT"),
      threshold: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
      visitCooldownHours: z.number().int().min(1).max(720),
      includeHistorical: z.boolean(),
    })
    .strict(),
]);
const rewardAutomationInactivityTriggerSchema = z
  .object({
    cadence: rewardAutomationCadenceSchema,
    inactivityDays: z.number().int().min(1).max(3_650),
  })
  .strict();
const rewardAutomationExpiryTriggerSchema = z
  .object({
    cadence: rewardAutomationCadenceSchema,
    daysBefore: z.number().int().min(1).max(365),
  })
  .strict();
const rewardAutomationTierTriggerSchema = z
  .object({
    cadence: rewardAutomationCadenceSchema,
    tierCode: z.string().regex(/^MERO(?:_[A-Z][A-Z0-9_]*)?$/),
    includeHistorical: z.boolean(),
  })
  .strict();
const rewardAutomationSpecialEventTriggerSchema = z
  .object({
    cadence: rewardAutomationCadenceSchema,
    eventDate: z.string().refine(isValidAutomationCalendarDate, {
      message: "Special-event date must be a valid YYYY-MM-DD date.",
    }),
    repeatAnnually: z.boolean(),
  })
  .strict();

const executableAutomationVariant = <Code extends (typeof rewardAutomationTemplateCodes)[number]>(
  templateCode: Code,
  trigger: z.ZodType<Record<string, unknown>>,
  action: z.ZodType<Record<string, unknown>>,
) =>
  z
    .object({
      ...rewardAutomationCommonShape,
      templateCode: z.literal(templateCode),
      trigger,
      action,
    })
    .strict();

const rewardAutomationDraftOnlyCommonShape = {
  ...rewardAutomationCommonShape,
  rewardPublicId: z.null().optional(),
  frequencyCapDays: z.null().optional(),
  status: z.literal("DRAFT").default("DRAFT"),
  nextRunAt: z.null().optional(),
  trigger: z.object({ executionMode: z.literal("DRAFT_ONLY") }).strict(),
};

const rewardAutomationConfigurationSchema = z.discriminatedUnion("templateCode", [
  executableAutomationVariant(
    "WELCOME",
    rewardAutomationWelcomeTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "FIRST_PURCHASE",
    rewardAutomationFirstPurchaseTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "BIRTHDAY",
    rewardAutomationCalendarTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "MEMBERSHIP_ANNIVERSARY",
    rewardAutomationCalendarTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "MILESTONE",
    rewardAutomationMilestoneTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "INACTIVITY",
    rewardAutomationInactivityTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "REWARD_EXPIRING",
    rewardAutomationExpiryTriggerSchema,
    rewardAutomationReminderActionSchema,
  ),
  executableAutomationVariant(
    "POINTS_EXPIRING",
    rewardAutomationExpiryTriggerSchema,
    rewardAutomationReminderActionSchema,
  ),
  executableAutomationVariant(
    "TIER_STATUS",
    rewardAutomationTierTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  executableAutomationVariant(
    "SPECIAL_EVENT",
    rewardAutomationSpecialEventTriggerSchema,
    rewardAutomationIssueActionSchema,
  ),
  z
    .object({
      ...rewardAutomationDraftOnlyCommonShape,
      templateCode: z.literal("REFERRAL_QUALIFIED"),
      action: z
        .object({
          type: z.literal("QUALIFIED_REFERRAL_EVENT_REQUIRED"),
          reason: rewardAutomationReasonSchema,
          channels: rewardAutomationChannelsSchema,
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...rewardAutomationDraftOnlyCommonShape,
      templateCode: z.literal("SERVICE_RECOVERY"),
      action: z
        .object({
          type: z.literal("MANUAL_ONLY"),
          reason: rewardAutomationReasonSchema,
          channels: rewardAutomationChannelsSchema,
        })
        .strict(),
    })
    .strict(),
]);

export const rewardAutomationCreateSchema = rewardAutomationConfigurationSchema.superRefine(
  (value, context) => {
    const issuesReward = ![
      "REWARD_EXPIRING",
      "POINTS_EXPIRING",
      "REFERRAL_QUALIFIED",
      "SERVICE_RECOVERY",
    ].includes(value.templateCode);
    if (issuesReward && value.status === "ACTIVE" && !value.rewardPublicId) {
      context.addIssue({
        code: "custom",
        path: ["rewardPublicId"],
        message: "An active reward-issuing automation must reference a reward.",
      });
    }
    if (!issuesReward && value.rewardPublicId) {
      context.addIssue({
        code: "custom",
        path: ["rewardPublicId"],
        message: "This automation template cannot reference a reward.",
      });
    }
  },
);

export const rewardAutomationUpdateSchema = z
  .object({
    name: rewardAutomationNameSchema.optional(),
    rewardPublicId: rewardAutomationPublicReferenceSchema.nullable().optional(),
    templateCode: z.enum(rewardAutomationTemplateCodes).optional(),
    trigger: z.record(z.string(), z.unknown()).optional(),
    action: z.record(z.string(), z.unknown()).optional(),
    timezone: rewardAutomationTimezoneSchema.optional(),
    frequencyCapDays: rewardAutomationFrequencyCapSchema,
    nextRunAt: rewardAutomationNextRunSchema,
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one automation field to update.",
  });

export const rewardAutomationStatusUpdateSchema = z
  .object({
    status: z.enum(["ACTIVE", "PAUSED", "ARCHIVED"]),
  })
  .strict();

export type RewardAutomationCreateInput = z.infer<typeof rewardAutomationCreateSchema>;
export type RewardAutomationUpdateInput = z.infer<typeof rewardAutomationUpdateSchema>;
export type RewardAutomationStatusUpdateInput = z.infer<typeof rewardAutomationStatusUpdateSchema>;
export type RewardAutomationLifecycleStatus = (typeof rewardAutomationLifecycleStatuses)[number];

export const rewardAnalyticsQuerySchema = z
  .object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.from || !value.to) return;
    const from = new Date(value.from).getTime();
    const to = new Date(value.to).getTime();
    if (from >= to) {
      context.addIssue({
        code: "custom",
        path: ["to"],
        message: "Analytics end time must be after the start time.",
      });
    }
    if (to - from > 366 * 24 * 60 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        path: ["to"],
        message: "Analytics ranges cannot exceed 366 days.",
      });
    }
  });

export type RewardAnalyticsQuery = z.infer<typeof rewardAnalyticsQuerySchema>;

export interface PublicReward {
  publicId: string;
  name: string;
  description: string;
  imageUrl: string | null;
  pointsCost: number;
  rewardType: string;
  mechanismConfig: Record<string, unknown>;
  mechanismTerms: string[];
  rewardValueMinor: number;
  minimumSpendMinor: number;
  maximumDiscountMinor: number | null;
  currencyCode: string;
  expirationMode: string;
  expirationDaysAfterIssue: number | null;
  termsText: string;
  eligibleTierCodes: string[];
  eligibleLocationPublicIds: string[];
  eligibleLocationNames: string[];
  channelScope: string[];
  perMemberLimit: number;
  lifecycleStatus: string;
  inventoryRemaining: number;
  featured: boolean;
  availableUntil: string | null;
  tenant: {
    publicId: string;
    slug: string;
    businessName: string;
    logoUrl: string | null;
    primaryColor: string;
  };
}

export const checkoutSourceTypes = [
  "MERCHANT_SCAN",
  "CUSTOMER_REQUEST",
  "CLAIM_CODE",
  "RECEIPT",
  "OFFLINE",
  "POS_API",
  "CSV",
] as const;

export const checkoutStatuses = [
  "PENDING",
  "PENDING_CUSTOMER",
  "PENDING_MANAGER",
  "CONFIRMED",
  "REJECTED",
  "REVERSED",
  "REVIEW",
] as const;

export const paymentTypes = ["CASH", "CARD", "QR_PAYMENT", "OTHER"] as const;

const checkoutPaymentTypeSchema = z.preprocess((value) => {
  if (typeof value !== "string") return value;
  const normalized = value.trim().toUpperCase();
  if (normalized === "DIGITAL_WALLET" || normalized === "WALLET") return "QR_PAYMENT";
  return normalized;
}, z.enum(paymentTypes));

const optionalRewardPointsToRedeemSchema = z.preprocess(
  (value) => (value === 0 || value === "" || value === null ? undefined : value),
  z.number().int().min(1).max(100_000_000).optional(),
);

const optionalMeroCreditsToRedeemMinorSchema = z.preprocess(
  (value) => (value === 0 || value === "" || value === null ? undefined : value),
  z.number().int().min(1).max(100_000_000_000).optional(),
);

const moneyMinor = z.number().int().min(1).max(100_000_000_000);
const publicReference = z.string().trim().min(1).max(160);
export const checkoutRewardChannels = [
  "IN_STORE",
  "ONLINE",
  "POS_API",
  "HOTEL_DESK",
  "CAFE_COUNTER",
  "MOBILE_APP",
] as const;
const checkoutLineItemsSchema = z
  .array(
    z
      .object({
        productCode: rewardMechanismCodeSchema.optional(),
        categoryCode: rewardMechanismCodeSchema.optional(),
        quantity: z.number().int().min(1).max(10_000),
      })
      .strict()
      .refine((item) => item.productCode !== undefined || item.categoryCode !== undefined, {
        message: "Each line item needs a product or category code.",
      }),
  )
  .max(100);

export const checkoutTransactionCreateSchema = z
  .object({
    tenantSlug: tenantSlugSchema.optional(),
    locationPublicId: publicReference,
    customerCode: publicReference.optional(),
    grossAmountMinor: moneyMinor,
    qualifyingAmountMinor: moneyMinor.optional(),
    currencyCode: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toUpperCase())
      .optional(),
    paymentType: checkoutPaymentTypeSchema.default("CASH"),
    merchantReference: z.string().trim().max(120).optional(),
    customerReference: z.string().trim().max(120).optional(),
    sourceType: z.enum(["MERCHANT_SCAN", "CUSTOMER_REQUEST", "POS_API", "CSV"]).optional(),
    channel: z.enum(checkoutRewardChannels).optional(),
    lineItems: checkoutLineItemsSchema.optional(),
    rewardPointsToRedeem: optionalRewardPointsToRedeemSchema,
    meroCreditsToRedeemMinor: optionalMeroCreditsToRedeemMinorSchema,
    occurredAt: z.iso.datetime().optional(),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .refine(
    (value) =>
      value.qualifyingAmountMinor === undefined ||
      value.qualifyingAmountMinor <= value.grossAmountMinor,
    {
      message: "Qualifying amount cannot exceed the gross amount.",
      path: ["qualifyingAmountMinor"],
    },
  );

export const checkoutRedemptionQuoteQuerySchema = z.object({
  customerCode: publicReference,
  locationPublicId: publicReference,
  // A zero-value quote is used by checkout clients to show the member's
  // business-specific balance before the cashier enters the purchase total.
  grossAmountMinor: z.coerce.number().int().min(0).max(100_000_000_000),
});

export const businessRedemptionRuleUpdateSchema = z
  .object({
    pointsPerUnit: z.number().int().min(1).max(100_000_000),
    valuePerUnitMinor: moneyMinor,
    minimumRedemptionPoints: z.number().int().min(0).max(100_000_000),
    maximumRedemptionPointsPerTransaction: z.number().int().min(1).max(100_000_000).nullable(),
  })
  .superRefine((value, context) => {
    if (value.minimumRedemptionPoints % value.pointsPerUnit !== 0) {
      context.addIssue({
        code: "custom",
        path: ["minimumRedemptionPoints"],
        message: "The minimum must be zero or a multiple of the redemption point increment.",
      });
    }
    if (
      value.maximumRedemptionPointsPerTransaction !== null &&
      value.maximumRedemptionPointsPerTransaction % value.pointsPerUnit !== 0
    ) {
      context.addIssue({
        code: "custom",
        path: ["maximumRedemptionPointsPerTransaction"],
        message: "The maximum must be a multiple of the redemption point increment.",
      });
    }
    if (
      value.maximumRedemptionPointsPerTransaction !== null &&
      value.maximumRedemptionPointsPerTransaction < value.minimumRedemptionPoints
    ) {
      context.addIssue({
        code: "custom",
        path: ["maximumRedemptionPointsPerTransaction"],
        message: "The maximum cannot be lower than the minimum.",
      });
    }
  });

export type BusinessRedemptionRuleUpdateInput = z.infer<typeof businessRedemptionRuleUpdateSchema>;

export const checkoutDecisionSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const checkoutReversalSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  idempotencyKey: z.string().trim().min(8).max(128),
});

export const claimIssueSchema = z
  .object({
    locationPublicId: publicReference,
    grossAmountMinor: moneyMinor,
    qualifyingAmountMinor: moneyMinor.optional(),
    paymentType: z.enum(paymentTypes).default("CASH"),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .refine(
    (value) =>
      value.qualifyingAmountMinor === undefined ||
      value.qualifyingAmountMinor <= value.grossAmountMinor,
    {
      message: "Qualifying amount cannot exceed the gross amount.",
      path: ["qualifyingAmountMinor"],
    },
  );

export const claimRedeemSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Enter the six-digit checkout code."),
  idempotencyKey: z.string().trim().min(8).max(128),
});

export const receiptSubmissionSchema = z
  .object({
    tenantSlug: tenantSlugSchema,
    locationPublicId: publicReference.optional(),
    grossAmountMinor: moneyMinor,
    qualifyingAmountMinor: moneyMinor.optional(),
    currencyCode: z
      .string()
      .trim()
      .length(3)
      .transform((value) => value.toUpperCase())
      .optional(),
    merchantReference: z.string().trim().max(120).optional(),
  })
  .refine(
    (value) =>
      value.qualifyingAmountMinor === undefined ||
      value.qualifyingAmountMinor <= value.grossAmountMinor,
    {
      message: "Qualifying amount cannot exceed the gross amount.",
      path: ["qualifyingAmountMinor"],
    },
  );

export const receiptReviewSchema = z
  .object({
    action: z.enum(["approved", "rejected"]),
    qualifyingAmountMinor: moneyMinor.optional(),
    reason: z.string().trim().max(500).optional(),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .refine((value) => value.action === "approved" || Boolean(value.reason), {
    message: "A rejection reason is required.",
    path: ["reason"],
  });

export const offlineSyncSchema = z.object({
  events: z
    .array(
      z.object({
        clientNonce: z.string().trim().min(8).max(128),
        locationPublicId: publicReference,
        customerCode: publicReference,
        grossAmountMinor: moneyMinor,
        qualifyingAmountMinor: moneyMinor.optional(),
        paymentType: z.enum(paymentTypes).default("CASH"),
        channel: z.enum(checkoutRewardChannels).optional(),
        lineItems: checkoutLineItemsSchema.optional(),
        occurredAt: z.iso.datetime(),
        idempotencyKey: z.string().trim().min(8).max(128),
      }),
    )
    .min(1)
    .max(25),
});

export const checkoutSettingsUpdateSchema = z
  .object({
    locationPublicId: publicReference.nullable().optional(),
    customerConfirmationThresholdMinor: z.number().int().min(0).max(100_000_000_000),
    managerApprovalThresholdMinor: z.number().int().min(0).max(100_000_000_000),
    maximumTransactionMinor: moneyMinor,
    maximumTransactionsPerCustomerDay: z.number().int().min(1).max(1_000),
    claimCodeTtlSeconds: z.number().int().min(60).max(1_800),
    receiptRecoveryWindowDays: z.number().int().min(1).max(90),
    offlineModeEnabled: z.boolean(),
  })
  .strict();

export const rewardPointsProgramUpdateSchema = z
  .object({
    spendUnitMinor: moneyMinor,
    pointsPerUnit: z.number().int().min(1).max(1_000_000),
    minimumQualifyingMinor: z.number().int().min(0).max(100_000_000_000),
    maximumPointsPerTransaction: z.number().int().min(1).max(100_000_000).nullable(),
    pointExpirationDays: z.number().int().min(1).max(3_650).nullable(),
  })
  .strict();

export type RewardPointsProgramUpdateInput = z.infer<typeof rewardPointsProgramUpdateSchema>;

export const checkoutListQuerySchema = z.object({
  status: z.enum(checkoutStatuses).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});

export const platformTierRuleCreateSchema = z.object({
  spendUnitMinor: moneyMinor,
  tierPointsPerUnit: z.number().int().min(1).max(1_000_000),
  minimumQualifyingMinor: z.number().int().min(0).max(100_000_000_000),
  maximumTierPointsPerTransaction: z.number().int().min(1).max(100_000_000).nullable().optional(),
  effectiveFrom: z.iso.datetime(),
});

export const platformTierRuleUpdateSchema = platformTierRuleCreateSchema;

export const tierAssessmentSchema = z.object({
  userPublicId: publicReference.optional(),
});

export const meroCreditAudiences = ["CUSTOMER", "BUSINESS", "BOTH"] as const;
export const meroCreditTriggerTypes = [
  "VERIFIED_FIRST_SIGNUP",
  "TENANT_APPROVED",
  "FIRST_QUALIFIED_PURCHASE",
  "TIER_PROMOTED",
  "REFERRAL_QUALIFIED",
  "BIRTHDAY",
  "MEMBERSHIP_ANNIVERSARY",
  "TRANSACTION_MILESTONE",
  "CROSS_BUSINESS_CHALLENGE",
  "CAMPAIGN",
  "ADMIN_EVENT",
] as const;
export const meroCreditFundingBuckets = [
  "PURCHASED",
  "PROMOTIONAL",
  "TIER_REWARD",
  "CAMPAIGN",
  "BUSINESS_INCENTIVE",
  "ADMIN_ADJUSTMENT",
] as const;

const meroCreditMoneyMinor = z.number().int().min(1).max(100_000_000_000);
const meroCreditCode = z
  .string()
  .trim()
  .min(2)
  .max(80)
  .regex(/^[A-Z0-9_]+$/);

export const meroCreditConfigurationSchema = z
  .object({
    regulatoryState: z.enum(["BLOCKED", "APPROVED"]),
    purchasingEnabled: z.boolean(),
    customerPurchasingEnabled: z.boolean(),
    businessPurchasingEnabled: z.boolean(),
    globalRedemptionEnabled: z.boolean(),
    minimumPurchaseMinor: meroCreditMoneyMinor,
    maximumPurchaseMinor: meroCreditMoneyMinor,
    dailyPurchaseLimitMinor: meroCreditMoneyMinor,
    monthlyPurchaseLimitMinor: meroCreditMoneyMinor,
    allowedDenominationsMinor: z.array(meroCreditMoneyMinor).max(20),
    customAmountEnabled: z.boolean(),
    approvedProviders: z.array(z.string().trim().min(2).max(80)).max(20),
    refundMode: z.enum(["DISABLED", "UNUSED_ONLY", "PARTIAL_CONTROLLED"]),
    purchasedExpiryDays: z.number().int().min(1).max(3_650).nullable(),
    promotionalDefaultExpiryDays: z.number().int().min(1).max(3_650),
    adjustmentDualApprovalThresholdMinor: meroCreditMoneyMinor,
    regulatoryApprovalReference: z.string().trim().min(3).max(500).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.maximumPurchaseMinor < value.minimumPurchaseMinor) {
      context.addIssue({
        code: "custom",
        path: ["maximumPurchaseMinor"],
        message: "Maximum purchase must be at least the minimum.",
      });
    }
    if (value.monthlyPurchaseLimitMinor < value.dailyPurchaseLimitMinor) {
      context.addIssue({
        code: "custom",
        path: ["monthlyPurchaseLimitMinor"],
        message: "Monthly limit must be at least the daily limit.",
      });
    }
    if (
      value.regulatoryState !== "APPROVED" &&
      (value.purchasingEnabled || value.globalRedemptionEnabled)
    ) {
      context.addIssue({
        code: "custom",
        path: ["regulatoryState"],
        message: "Purchasing and global redemption require an approved regulatory state.",
      });
    }
    if (value.regulatoryState === "APPROVED" && !value.regulatoryApprovalReference) {
      context.addIssue({
        code: "custom",
        path: ["regulatoryApprovalReference"],
        message: "Record the approval reference before enabling regulated features.",
      });
    }
  });

const meroCreditAwardBuckets = [
  "PROMOTIONAL",
  "TIER_REWARD",
  "CAMPAIGN",
  "BUSINESS_INCENTIVE",
  "ADMIN_ADJUSTMENT",
] as const;

const meroCreditRuleBaseSchema = z
  .object({
    code: meroCreditCode,
    name: z.string().trim().min(3).max(160),
    description: z.string().trim().min(10).max(1_000),
    audience: z.enum(meroCreditAudiences),
    triggerType: z.enum(meroCreditTriggerTypes),
    awardMinor: meroCreditMoneyMinor,
    fundingBucket: z.enum(meroCreditAwardBuckets),
    eligibility: z.record(z.string(), z.unknown()).default({}),
    frequencyType: z
      .enum(["ONCE_LIFETIME", "ONCE_PER_EVENT", "DAILY", "MONTHLY", "LIFETIME_CAPPED"])
      .default("ONCE_LIFETIME"),
    maximumAwardsPerHolder: z.number().int().min(1).max(100_000).nullable().default(1),
    totalBudgetMinor: meroCreditMoneyMinor.nullable().default(null),
    expiryMode: z.enum(["NEVER", "DAYS", "FIXED_DATE"]).default("DAYS"),
    expiryDays: z.number().int().min(1).max(3_650).nullable().default(90),
    expiresAt: z.iso.datetime().nullable().default(null),
    stackingMode: z.enum(["STACKABLE", "EXCLUSIVE", "PRIORITY"]).default("STACKABLE"),
    priority: z.number().int().min(1).max(1_000).default(100),
    effectiveFrom: z.iso.datetime(),
    effectiveTo: z.iso.datetime().nullable().default(null),
    changeReason: z.string().trim().min(5).max(1_000),
  })
  .strict();

export const meroCreditRuleCreateSchema = meroCreditRuleBaseSchema.superRefine((value, context) => {
  if (value.expiryMode === "DAYS" && value.expiryDays === null) {
    context.addIssue({
      code: "custom",
      path: ["expiryDays"],
      message: "Expiry days are required.",
    });
  }
  if (value.expiryMode === "FIXED_DATE" && value.expiresAt === null) {
    context.addIssue({
      code: "custom",
      path: ["expiresAt"],
      message: "A fixed expiry date is required.",
    });
  }
  if (value.effectiveTo && value.effectiveTo <= value.effectiveFrom) {
    context.addIssue({
      code: "custom",
      path: ["effectiveTo"],
      message: "Effective end must be after the start.",
    });
  }
});

export const meroCreditRuleVersionSchema = meroCreditRuleBaseSchema
  .omit({
    code: true,
    name: true,
    description: true,
    audience: true,
    triggerType: true,
  })
  .superRefine((value, context) => {
    if (value.expiryMode === "DAYS" && value.expiryDays === null) {
      context.addIssue({
        code: "custom",
        path: ["expiryDays"],
        message: "Expiry days are required.",
      });
    }
    if (value.expiryMode === "FIXED_DATE" && value.expiresAt === null) {
      context.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "A fixed expiry date is required.",
      });
    }
    if (value.effectiveTo && value.effectiveTo <= value.effectiveFrom) {
      context.addIssue({
        code: "custom",
        path: ["effectiveTo"],
        message: "Effective end must be after the start.",
      });
    }
  });

export const meroCreditPurchaseSchema = z
  .object({
    amountMinor: meroCreditMoneyMinor,
    providerCode: z.string().trim().min(2).max(80),
    purpose: z.string().trim().min(3).max(240).optional(),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .strict();

export const meroCreditRedemptionQuoteSchema = z
  .object({
    tenantSlug: tenantSlugSchema,
    transactionPublicId: publicReference,
    amountMinor: meroCreditMoneyMinor,
  })
  .strict();

export const meroCreditRedemptionConfirmSchema = z
  .object({
    token: z.string().trim().min(32).max(160),
    idempotencyKey: z.string().trim().min(8).max(128),
  })
  .strict();

export const meroCreditAdjustmentSchema = z
  .object({
    holderType: z.enum(["CUSTOMER", "BUSINESS"]),
    holderPublicId: publicReference,
    amountMinor: z
      .number()
      .int()
      .min(-100_000_000_000)
      .max(100_000_000_000)
      .refine((value) => value !== 0),
    fundingBucket: z.enum(["ADMIN_ADJUSTMENT", "PROMOTIONAL"]).default("ADMIN_ADJUSTMENT"),
    reason: z.string().trim().min(10).max(1_000),
  })
  .strict();

export const meroCreditAcceptanceSchema = z
  .object({
    enabled: z.boolean(),
    minimumBasketMinor: meroCreditMoneyMinor,
    maximumPaymentBasisPoints: z.number().int().min(1).max(10_000),
    minimumRedemptionMinor: meroCreditMoneyMinor,
    maximumRedemptionMinor: meroCreditMoneyMinor,
    excludedCategoryCodes: z.array(z.string().trim().min(1).max(80)).max(200),
    eligibleLocationPublicIds: z.array(publicReference).max(200),
  })
  .strict()
  .refine((value) => value.maximumRedemptionMinor >= value.minimumRedemptionMinor, {
    message: "Maximum redemption must be at least the minimum.",
    path: ["maximumRedemptionMinor"],
  });

export type MeroCreditConfigurationInput = z.infer<typeof meroCreditConfigurationSchema>;
export type MeroCreditRuleCreateInput = z.infer<typeof meroCreditRuleCreateSchema>;
export type MeroCreditRuleVersionInput = z.infer<typeof meroCreditRuleVersionSchema>;
export type MeroCreditPurchaseInput = z.infer<typeof meroCreditPurchaseSchema>;
export type MeroCreditRedemptionQuoteInput = z.infer<typeof meroCreditRedemptionQuoteSchema>;
export type MeroCreditRedemptionConfirmInput = z.infer<typeof meroCreditRedemptionConfirmSchema>;
export type MeroCreditAdjustmentInput = z.infer<typeof meroCreditAdjustmentSchema>;
export type MeroCreditAcceptanceInput = z.infer<typeof meroCreditAcceptanceSchema>;
export type PlatformUserUpdateInput = z.infer<typeof platformUserUpdateSchema>;
export type PlatformSettingsInput = z.infer<typeof platformSettingsSchema>;
